import { type FC, useEffect, useState } from 'react';
import { Form } from 'antd';
import { Modal, Select, Textarea } from '@g2u/ui-kit';

import { validateFormSilently } from 'helpers';
import { SkillLevel, TechnicianSkillSet } from 'types';

const NO_SKILLSETS_AVAILABLE_VALUE = '';

const skillLevelOptions = [
  { value: SkillLevel.None.toString(), label: 'None' },
  { value: SkillLevel.Proficient.toString(), label: 'Proficient' },
  { value: SkillLevel.Expert.toString(), label: 'Expert' },
];

type Props = {
  skillset: TechnicianSkillSet | null;
  assignableSkillSets: TechnicianSkillSet[];
  isOpen: boolean;
  isLoading: boolean;
  onSubmit: (values: SkillsetModal.FormValues) => void;
  onClose: () => void;
};

export const SkillsetModal: FC<Props> = (props) => {
  const { skillset, assignableSkillSets, isOpen, isLoading, onSubmit, onClose } = props;
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    if (isOpen) {
      validateFormSilently(form, setIsFormValid);
    }
  }, [isOpen, form]);

  const handleFormChange = () => {
    validateFormSilently(form, setIsFormValid);
  };

  const handleFormSubmit = (values: SkillsetModal.FormValues & { description?: string }) => {
    onSubmit({
      technicianSkillSetId: skillset?.ID,
      skillset_ID: Number(values.skillset_ID),
      level: Number(values.level) as SkillLevel,
    });
  };

  const handleSkillsetSelect = (skillsetId: string) => {
    const selected = assignableSkillSets.find((assignable) => assignable.skillset_ID === Number(skillsetId));
    form.setFieldValue('description', selected?.description);
  };

  const hasAssignableSkillSets = assignableSkillSets.length > 0;

  const skillsetOptions = skillset
    ? [{ value: skillset.skillset_ID.toString(), label: skillset.description }]
    : hasAssignableSkillSets
      ? assignableSkillSets.map((assignable) => ({ value: assignable.skillset_ID.toString(), label: assignable.description }))
      : [{ value: NO_SKILLSETS_AVAILABLE_VALUE, label: 'No Skillsets Available', disabled: true }];

  const skillsetInitialValue = skillset
    ? skillset.skillset_ID.toString()
    : hasAssignableSkillSets
      ? undefined
      : NO_SKILLSETS_AVAILABLE_VALUE;

  return (
    <Modal.$
      dataTestId="technician-skillset-modal"
      open={isOpen}
      title={skillset ? 'Edit Skillset' : 'New Skillset'}
      okText="Save"
      onCancel={onClose}
      onOk={form.submit}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: !isFormValid || isLoading }}
    >
      <Form
        form={form}
        onFinish={handleFormSubmit}
        onValuesChange={handleFormChange}
        labelCol={{ style: { width: 130 } }}
        labelAlign="left"
        layout="horizontal"
        disabled={isLoading}
        preserve={false}
      >
        <Select.$
          name="skillset_ID"
          label="Skillset"
          rules={[{ required: true, message: 'Skillset is required.' }]}
          required
          disabled={!!skillset}
          initialValue={skillsetInitialValue}
          onSelect={handleSkillsetSelect}
          options={skillsetOptions}
        />
        <Textarea.$ name="description" label="Description" disabled initialValue={skillset?.description} />
        <Select.$
          name="level"
          label="Level"
          rules={[
            { required: true, message: 'Level is required.' },
            {
              validator: (_, value) =>
                value === SkillLevel.None.toString() ? Promise.reject(new Error('Level is required.')) : Promise.resolve(),
            },
          ]}
          required
          initialValue={(skillset?.level ?? SkillLevel.None).toString()}
          options={skillLevelOptions}
        />
      </Form>
    </Modal.$>
  );
};

export namespace SkillsetModal {
  export type FormValues = {
    skillset_ID: number;
    level: SkillLevel;
    technicianSkillSetId?: number;
  };
}
